import React, { useEffect, useRef, useState } from "react";
import { X, UserPlus, Upload, BadgeCheck, Crown, Star, Copy, Check, Music2, Link2, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { CATEGORIES as CATS } from "../lib/categories";

const CATEGORIES = [
  { value: "", label: "— Non définie —" },
  ...CATS.map((c) => ({ value: c.key, label: `${c.emoji} ${c.label}` })),
];

const inputCls = "w-full rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-orange/60";

/**
 * AdminCreateArtistModal
 * Permet à l'admin de créer un profil artiste depuis zéro (sans inscription).
 * Si mot de passe non fourni, le backend en génère un et le renvoie une seule fois.
 */
export const AdminCreateArtistModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    bio: "",
    category: "music",
    country: "Madagascar",
    city: "",
    picture: "",
    is_artist: true,
    is_verified: false,
    is_premium: false,
    is_featured: false,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generatedPass, setGeneratedPass] = useState(null);
  const [createdUser, setCreatedUser] = useState(null);
  const [copied, setCopied] = useState(false);
  const picRef = useRef(null);

  // YouTube channel auto-import
  const [ytUrl, setYtUrl] = useState("");
  const [ytMax, setYtMax] = useState(10);
  const [ytImporting, setYtImporting] = useState(false);
  const [ytResult, setYtResult] = useState(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const uploadPicture = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "profile");
    setUploading(true);
    try {
      const { data } = await api.post("/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, picture: data.url }));
      toast.success("Photo uploadée");
    } catch {
      toast.error("Upload échoué");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Le nom est requis"); return; }
    setSaving(true);
    let created = null;
    try {
      const { data } = await api.post("/admin/users", form);
      created = data.user;
      toast.success(`Artiste « ${created.name} » créé ✨`);
      onCreated?.(created);
      setCreatedUser(created);
      if (data.generated_password) setGeneratedPass(data.generated_password);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erreur création");
      setSaving(false);
      return;
    }
    setSaving(false);

    // Import YouTube channel si URL fournie
    if (created && ytUrl.trim()) {
      setYtImporting(true);
      try {
        const { data } = await api.post(
          `/admin/users/${created.user_id}/tracks/import-channel`,
          {
            channel_url: ytUrl.trim(),
            category: form.category || "music",
            max: ytMax,
          }
        );
        setYtResult(data);
        toast.success(`${data.summary.succeeded} vidéo${data.summary.succeeded > 1 ? "s" : ""} importée${data.summary.succeeded > 1 ? "s" : ""} 🎬`);
      } catch (err) {
        toast.error(err?.response?.data?.error || "Chaîne YouTube non trouvée");
      } finally {
        setYtImporting(false);
      }
    }

    // Si pas de password à afficher ET pas d'import → ferme directement
    if (created && !generatedPass && !ytUrl.trim()) {
      // On laisse le temps au toast
      setTimeout(() => onClose(), 500);
    }
  };

  const copyPass = async () => {
    try {
      await navigator.clipboard.writeText(`Email : ${createdUser.email}\nMot de passe : ${generatedPass}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Credentials copiés");
    } catch { toast.error("Impossible de copier"); }
  };

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/75 backdrop-blur-sm flex items-start sm:items-center justify-center p-0 sm:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="admin-create-artist-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-3xl sm:rounded-2xl border border-white/10 bg-[#0d0706] shadow-2xl my-4 flex flex-col max-h-[95vh]"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#0d0706] z-10">
          <div className="flex items-center gap-2">
            <UserPlus size={18} className="text-brand-orange" />
            <div>
              <p className="text-[10px] uppercase tracking-widest text-brand-orange font-bold">Création</p>
              <h3 className="font-display text-xl font-black">Nouveau profil artiste</h3>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10" aria-label="Fermer" data-testid="admin-create-close">
            <X size={18} />
          </button>
        </div>

        {/* Post-création : mot de passe et/ou résultat import YouTube */}
        {createdUser ? (
          <div className="p-6 space-y-5">
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4">
              <p className="font-bold text-emerald-400 mb-1">✓ Profil « {createdUser.name} » créé</p>
              {generatedPass && (
                <p className="text-sm text-white/70">Note ces identifiants — ils ne seront plus jamais affichés.</p>
              )}
            </div>

            {generatedPass && (
              <div className="rounded-xl bg-black/40 border border-white/10 p-4 font-mono text-sm space-y-2" data-testid="admin-creds-block">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Email</p>
                  <p className="text-white">{createdUser.email}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Mot de passe temporaire</p>
                  <p className="text-brand-yellow font-bold tracking-wider">{generatedPass}</p>
                </div>
              </div>
            )}

            {/* Import YouTube status */}
            {ytUrl.trim() && (
              <div className="rounded-xl bg-black/30 border border-white/10 p-4" data-testid="admin-yt-result">
                <div className="flex items-center gap-2 mb-2">
                  <Link2 size={14} className="text-brand-red" />
                  <p className="text-xs uppercase tracking-widest text-brand-red font-bold">Import chaîne YouTube</p>
                </div>
                {ytImporting ? (
                  <p className="text-sm text-white/70 inline-flex items-center gap-2">
                    <Loader2 size={13} className="animate-spin" /> Scraping en cours…
                  </p>
                ) : ytResult ? (
                  <div className="space-y-2">
                    <p className="text-sm">
                      <span className="text-brand-yellow font-bold">{ytResult.channel_title || "Chaîne"}</span>
                      {" — "}
                      <span className="text-emerald-400 font-bold">{ytResult.summary.succeeded}</span> importée{ytResult.summary.succeeded > 1 ? "s" : ""}
                      {ytResult.summary.skipped > 0 && <>, <span className="text-white/50">{ytResult.summary.skipped} ignorée{ytResult.summary.skipped > 1 ? "s" : ""}</span></>}
                      {ytResult.summary.failed > 0 && <>, <span className="text-red-400">{ytResult.summary.failed} échec{ytResult.summary.failed > 1 ? "s" : ""}</span></>}
                    </p>
                    {ytResult.created?.length > 0 && (
                      <ul className="space-y-0.5 max-h-40 overflow-y-auto">
                        {ytResult.created.map((c) => (
                          <li key={c.track_id} className="text-xs text-white/70 flex items-center gap-1.5">
                            <CheckCircle2 size={11} className="text-emerald-400 shrink-0" />
                            <span className="truncate">{c.title}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-red-300">Import échoué.</p>
                )}
              </div>
            )}

            <div className="flex gap-2">
              {generatedPass && (
                <button onClick={copyPass} className="btn-primary inline-flex items-center gap-1.5" data-testid="admin-copy-creds">
                  {copied ? <><Check size={14} /> Copié</> : <><Copy size={14} /> Copier les identifiants</>}
                </button>
              )}
              <button onClick={onClose} className="btn-ghost ml-auto" data-testid="admin-create-done">
                Terminer
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="overflow-y-auto p-6 space-y-6">
            {/* Photo + basics */}
            <div className="grid sm:grid-cols-[120px_1fr] gap-4">
              <div className="flex flex-col items-center gap-2">
                {form.picture ? (
                  <img src={form.picture} alt="" className="w-28 h-28 rounded-xl object-cover border border-white/10" />
                ) : (
                  <div className="w-28 h-28 rounded-xl bg-gradient-to-br from-brand-red to-brand-orange flex items-center justify-center font-black text-3xl">
                    {(form.name || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                <input
                  ref={picRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && uploadPicture(e.target.files[0])}
                  data-testid="admin-create-pic-input"
                />
                <button
                  type="button"
                  onClick={() => picRef.current?.click()}
                  disabled={uploading}
                  className="text-xs btn-ghost inline-flex items-center gap-1 px-3 py-1.5"
                  data-testid="admin-create-pic-upload"
                >
                  <Upload size={11} /> {uploading ? "…" : "Photo"}
                </button>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Nom d'artiste *">
                  <input required value={form.name} onChange={set("name")} className={inputCls} placeholder="Ex: Rossy" data-testid="admin-create-name" />
                </Field>
                <Field label="Catégorie">
                  <select value={form.category} onChange={set("category")} className={inputCls} data-testid="admin-create-category">
                    {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </Field>
                <Field label="Email (optionnel)">
                  <input type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="auto-généré si vide" data-testid="admin-create-email" />
                </Field>
                <Field label="Mot de passe (optionnel)">
                  <input value={form.password} onChange={set("password")} className={inputCls} placeholder="auto-généré si vide (min 6)" data-testid="admin-create-password" />
                </Field>
                <Field label="Pays">
                  <input value={form.country} onChange={set("country")} className={inputCls} data-testid="admin-create-country" />
                </Field>
                <Field label="Ville">
                  <input value={form.city} onChange={set("city")} className={inputCls} placeholder="Antananarivo, Toamasina…" data-testid="admin-create-city" />
                </Field>
                <Field label="Bio" full>
                  <textarea value={form.bio} onChange={set("bio")} rows={2} className={inputCls} placeholder="Un paragraphe court qui résume l'artiste" data-testid="admin-create-bio" />
                </Field>
                <Field label="URL photo (alternative au upload)" full>
                  <input value={form.picture} onChange={set("picture")} className={inputCls} placeholder="https://…" data-testid="admin-create-picture-url" />
                </Field>
              </div>
            </div>

            {/* Badges */}
            <div>
              <p className="text-xs uppercase tracking-widest text-white/50 font-bold mb-2">Badges à activer</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Toggle label="Artiste" icon={Music2} active={form.is_artist}   onToggle={() => setForm((f) => ({ ...f, is_artist: !f.is_artist }))} testid="toggle-artist" />
                <Toggle label="Vérifié" icon={BadgeCheck} active={form.is_verified} onToggle={() => setForm((f) => ({ ...f, is_verified: !f.is_verified }))} color="text-brand-yellow" testid="toggle-verified" />
                <Toggle label="Premium" icon={Crown}    active={form.is_premium}  onToggle={() => setForm((f) => ({ ...f, is_premium: !f.is_premium }))} color="text-brand-orange" testid="toggle-premium" />
                <Toggle label="Vedette" icon={Star}     active={form.is_featured} onToggle={() => setForm((f) => ({ ...f, is_featured: !f.is_featured }))} color="text-brand-red" testid="toggle-featured" />
              </div>
            </div>

            {/* YouTube auto-import */}
            <div className="rounded-xl border border-brand-red/30 bg-brand-red/5 p-4" data-testid="yt-import-section">
              <div className="flex items-center gap-2 mb-2">
                <Link2 size={14} className="text-brand-red" />
                <p className="text-xs uppercase tracking-widest text-brand-red font-bold">Combo : importer sa chaîne YouTube</p>
                <span className="text-[10px] text-white/40 ml-auto">optionnel</span>
              </div>
              <p className="text-xs text-white/60 mb-3">
                Si tu colles l'URL de sa chaîne (<code className="text-brand-yellow">@handle</code>, <code className="text-brand-yellow">/channel/UC…</code>, <code className="text-brand-yellow">/c/custom</code>),
                le backend scrape les X dernières vidéos et les publie à son nom dans la foulée.
              </p>
              <div className="grid sm:grid-cols-[1fr_100px] gap-2">
                <input
                  value={ytUrl}
                  onChange={(e) => setYtUrl(e.target.value)}
                  placeholder="https://youtube.com/@… (laisse vide pour ignorer)"
                  className={inputCls + " font-mono"}
                  data-testid="admin-create-yt-url"
                />
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={ytMax}
                  onChange={(e) => setYtMax(Math.max(1, Math.min(50, parseInt(e.target.value) || 10)))}
                  className={inputCls}
                  placeholder="max"
                  title="Max vidéos à importer (1-50)"
                  data-testid="admin-create-yt-max"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
              <button type="button" onClick={onClose} className="btn-ghost">Annuler</button>
              <button type="submit" disabled={saving} className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="admin-create-submit">
                <UserPlus size={14} /> {saving ? "Création…" : ytUrl.trim() ? "Créer + importer" : "Créer l'artiste"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="block text-[10px] uppercase tracking-widest text-white/50 mb-1 font-bold">{label}</label>
    {children}
  </div>
);

const Toggle = ({ label, icon: Icon, active, onToggle, color = "text-white", testid }) => (
  <button
    type="button"
    onClick={onToggle}
    className={`flex flex-col items-center gap-1 p-2.5 rounded-xl border transition-all ${
      active ? "border-brand-orange/40 bg-brand-orange/10" : "border-white/10 bg-white/[0.02] hover:border-white/25"
    }`}
    data-testid={testid}
  >
    <Icon size={16} className={active ? color : "text-white/40"} />
    <span className={`text-[10px] font-bold uppercase tracking-wider ${active ? "text-white" : "text-white/50"}`}>
      {label}
    </span>
  </button>
);

export default AdminCreateArtistModal;
